<template>
  <div class="exec-dash">
    <!-- ══ HEADER BAR ══ -->
    <div class="hdr-bar">
      <div class="hdr-left">
        <div class="hdr-icon-wrap">
          <v-icon size="28" color="white">mdi-chart-areaspline</v-icon>
        </div>
        <div>
          <div class="hdr-title">ศูนย์สารสนเทศผู้บริหาร — สอศ.</div>
          <div class="hdr-sub">
            ข้อมูลนักเรียน นักศึกษา และอัตรากำลังสถานศึกษา สังกัด
            สำนักงานคณะกรรมการการอาชีวศึกษา
          </div>
        </div>
      </div>
      <div class="hdr-right">
        <div class="hdr-pill hdr-pill--year">
          <v-icon size="14">mdi-calendar</v-icon>
          <span>พ.ศ. {{ filterYear }}</span>
        </div>
        <div class="hdr-pill hdr-pill--count">
          <v-icon size="14">mdi-account-group</v-icon>
          <strong>{{ formatNum(totalStudents) }}</strong
          >&nbsp;คน
        </div>
        <v-btn icon small dark @click="drawerOpen = !drawerOpen">
          <v-icon size="20">mdi-view-grid-outline</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- ══ FILTER BAR ══ -->
    <div class="filter-bar">
      <div class="filter-inner">
        <div class="filter-group">
          <span class="filter-label">ปีการศึกษา</span>
          <v-select
            v-model="filterYear"
            :items="yearOptions"
            dense
            outlined
            hide-details
            class="f-sel"
            @change="onFilterChange"
          />
        </div>
        <div class="filter-group">
          <span class="filter-label">ภาคเรียน</span>
          <v-select
            v-model="filterSemester"
            :items="semesterOptions"
            dense
            outlined
            hide-details
            class="f-sel"
            @change="onFilterChange"
          />
        </div>
        <div class="filter-group">
          <span class="filter-label">ภาค</span>
          <v-select
            v-model="filterRegion"
            :items="regionOptions"
            item-text="region_name"
            item-value="region_ID"
            dense
            outlined
            hide-details
            clearable
            class="f-sel f-sel--md"
            @change="onRegionChange"
          />
        </div>
        <div class="filter-group">
          <span class="filter-label">จังหวัด</span>
          <v-autocomplete
            v-model="filterProvince"
            :items="filteredProvinces"
            item-text="province_name"
            item-value="province_ID"
            dense
            outlined
            hide-details
            clearable
            class="f-sel f-sel--md"
            @change="onProvinceChange"
          />
        </div>
        <div class="filter-group">
          <span class="filter-label">สถานศึกษา</span>
          <v-autocomplete
            v-model="filterCollege"
            :items="collegeOptions"
            item-text="college_name"
            item-value="college_code"
            dense
            outlined
            hide-details
            clearable
            class="f-sel f-sel--lg"
            @change="buildAllCharts"
          />
        </div>
        <div class="filter-group">
          <span class="filter-label">ค้นหาสาขาวิชา</span>
          <v-text-field
            v-model="searchBranch"
            dense
            outlined
            hide-details
            clearable
            prepend-inner-icon="mdi-magnify"
            placeholder="พิมพ์ชื่อสาขา..."
            class="f-sel f-sel--lg"
          />
        </div>
        <div class="filter-actions">
          <v-btn small class="btn-refresh" :loading="loading" @click="fetchAll">
            <v-icon size="15" left>mdi-refresh</v-icon>รีเฟรช
          </v-btn>
          <v-btn small class="btn-export" @click="exportXlsx">
            <v-icon size="15" left>mdi-microsoft-excel</v-icon>ส่งออก xlsx
          </v-btn>
        </div>
      </div>
    </div>

    <!-- ══ LOADING ══ -->
    <v-overlay :value="loading" z-index="200" color="#3f3d7d" opacity=".55">
      <div class="loading-wrap">
        <v-progress-circular
          indeterminate
          color="#7c6dde"
          size="48"
          width="3"
        />
        <span class="loading-txt">กำลังโหลดข้อมูล...</span>
      </div>
    </v-overlay>

    <div class="dash-body">
      <!-- ══ ROW 1: KPI CARDS ══ -->
      <div class="kpi-row">
        <!-- นักเรียน ปวช. -->
        <div class="kpi-card kpi-card--blue">
          <div class="kpi-card-body">
            <div class="kpi-lbl">นักเรียน ปวช.</div>
            <div class="kpi-val">{{ formatNum(totalCh) }}</div>
            <div class="kpi-sub">
              {{
                totalStudents ? Math.round((totalCh / totalStudents) * 100) : 0
              }}% ของทั้งหมด
            </div>
            <v-progress-linear
              :value="totalStudents ? (totalCh / totalStudents) * 100 : 0"
              color="#2563eb"
              height="3"
              rounded
              background-color="rgba(37,99,235,.15)"
              class="mt-2"
            />
          </div>
          <div class="kpi-icon-wrap kpi-icon-wrap--blue">
            <v-icon size="28" color="white">mdi-account-school</v-icon>
          </div>
        </div>

        <!-- นักศึกษา ปวส. -->
        <div class="kpi-card kpi-card--purple">
          <div class="kpi-card-body">
            <div class="kpi-lbl">นักศึกษา ปวส.</div>
            <div class="kpi-val">{{ formatNum(totalHs) }}</div>
            <div class="kpi-sub">
              {{
                totalStudents ? Math.round((totalHs / totalStudents) * 100) : 0
              }}% ของทั้งหมด
            </div>
            <v-progress-linear
              :value="totalStudents ? (totalHs / totalStudents) * 100 : 0"
              color="#7c3aed"
              height="3"
              rounded
              background-color="rgba(124,58,237,.15)"
              class="mt-2"
            />
          </div>
          <div class="kpi-icon-wrap kpi-icon-wrap--purple">
            <v-icon size="28" color="white">mdi-certificate</v-icon>
          </div>
        </div>

        <!-- รวมทั้งหมด -->
        <div class="kpi-card kpi-card--teal">
          <div class="kpi-card-body">
            <div class="kpi-lbl">รวมทั้งหมด</div>
            <div class="kpi-val">{{ formatNum(totalStudents) }}</div>
            <div class="kpi-sub">นักเรียน นักศึกษา</div>
            <div class="kpi-trend" v-if="trendPct !== null">
              <v-icon size="13" :color="trendPct >= 0 ? '#059669' : '#dc2626'">
                {{ trendPct >= 0 ? "mdi-trending-up" : "mdi-trending-down" }}
              </v-icon>
              <span :class="trendPct >= 0 ? 'trend-pos' : 'trend-neg'">
                {{ trendPct >= 0 ? "+" : "" }}{{ trendPct }}%
              </span>
              <span class="trend-ref">เทียบปีก่อน</span>
            </div>
          </div>
          <div class="kpi-icon-wrap kpi-icon-wrap--teal">
            <v-icon size="28" color="white">mdi-account-group</v-icon>
          </div>
        </div>

        <!-- จำนวนสาขาวิชา -->
        <div class="kpi-card kpi-card--orange">
          <div class="kpi-card-body">
            <div class="kpi-lbl">จำนวนสาขาวิชา</div>
            <div class="kpi-val">{{ formatNum(totalBranches) }}</div>
            <div class="kpi-sub">{{ totalColleges }} สถานศึกษา</div>
            <v-progress-linear
              :value="100"
              color="#ea580c"
              height="3"
              rounded
              background-color="rgba(234,88,12,.15)"
              class="mt-2"
            />
          </div>
          <div class="kpi-icon-wrap kpi-icon-wrap--orange">
            <v-icon size="28" color="white">mdi-book-open-variant</v-icon>
          </div>
        </div>

        <!-- ข้าราชการครู + อัตราส่วน (ใช้ข้อมูลกรองแล้ว) -->
        <div class="kpi-card kpi-card--indigo">
          <div class="kpi-card-body">
            <div class="kpi-lbl">ข้าราชการครู</div>
            <div class="kpi-val">{{ formatNum(filteredTeachOfficial) }}</div>
            <div class="kpi-sub">อัตรา 1 : {{ teacherRatio }}</div>
            <v-progress-linear
              :value="
                filteredManpowerAll
                  ? (filteredTeachOfficial / filteredManpowerAll) * 100
                  : 0
              "
              color="#4338ca"
              height="3"
              rounded
              background-color="rgba(67,56,202,.15)"
              class="mt-2"
            />
          </div>
          <div class="kpi-icon-wrap kpi-icon-wrap--indigo">
            <v-icon size="28" color="white">mdi-human-male-board</v-icon>
          </div>
        </div>

        <!-- อัตรากำลังรวม -->
        <div class="kpi-card kpi-card--rose">
          <div class="kpi-card-body">
            <div class="kpi-lbl">อัตรากำลังรวม</div>
            <div class="kpi-val">{{ formatNum(filteredManpowerAll) }}</div>
            <div class="kpi-sub-row">
              <span
                :class="
                  filteredCriterionIncomplete < 0 ? 'badge-red' : 'badge-green'
                "
              >
                {{ filteredCriterionIncomplete < 0 ? "ขาด" : "เกิน" }}
                {{ formatNum(Math.abs(filteredCriterionIncomplete)) }}
              </span>
              <span class="kpi-sub"
                >จากเกณฑ์ {{ formatNum(filteredCriterionAll) }}</span
              >
            </div>
          </div>
          <div class="kpi-icon-wrap kpi-icon-wrap--rose">
            <v-icon size="28" color="white">mdi-badge-account</v-icon>
          </div>
        </div>
      </div>

      <!-- ══ ROW 2: CHARTS ══ -->
      <div class="dash-row">
        <!-- 2A: Student Level Bar -->
        <div class="panel" style="flex:2.2">
          <div class="panel-hd">
            <v-icon size="16" color="#2563eb" class="mr-2"
              >mdi-chart-bar</v-icon
            >
            <span>จำนวนนักเรียนแยกระดับ ปวช./ปวส.</span>
            <v-spacer />
            <div class="tab-grp">
              <span
                v-for="t in ['ภาค', 'ประเภทวิชา', 'จังหวัด']"
                :key="t"
                :class="['tab-btn', { active: levelMode === t }]"
                @click="
                  levelMode = t;
                  buildLevelChart();
                "
                >{{ t }}</span
              >
            </div>
          </div>
          <apexchart
            v-if="levelChart.series.length"
            type="bar"
            height="230"
            :options="levelChart.opts"
            :series="levelChart.series"
          />
          <div v-else class="no-data">ไม่มีข้อมูล</div>
        </div>

        <!-- 2B: Donut -->
        <div class="panel" style="flex:1">
          <div class="panel-hd">
            <v-icon size="16" color="#7c3aed" class="mr-2"
              >mdi-chart-donut</v-icon
            >
            <span>สัดส่วนระดับการศึกษา</span>
          </div>
          <apexchart
            v-if="totalStudents > 0"
            type="donut"
            height="200"
            :options="donutOpts"
            :series="[totalCh, totalHs]"
          />
          <div v-else class="no-data" style="height:200px">ไม่มีข้อมูล</div>
          <div class="donut-leg">
            <div class="dl-item">
              <span class="dl-dot" style="background:#2563eb"></span>
              <span>ปวช.</span><strong>{{ formatNum(totalCh) }}</strong>
            </div>
            <div class="dl-item">
              <span class="dl-dot" style="background:#7c3aed"></span>
              <span>ปวส.</span><strong>{{ formatNum(totalHs) }}</strong>
            </div>
          </div>
        </div>

        <!-- 2C: Manpower Status -->
        <div class="panel" style="flex:1">
          <div class="panel-hd">
            <v-icon size="16" color="#ea580c" class="mr-2"
              >mdi-clipboard-check</v-icon
            >
            <span>สถานะรายงานอัตรากำลัง</span>
          </div>
          <apexchart
            v-if="mpDonut.series.length"
            type="radialBar"
            height="200"
            :options="mpDonut.opts"
            :series="mpDonut.series"
          />
          <div v-else class="no-data" style="height:200px">ไม่มีข้อมูล</div>
          <div class="mp-stat-leg">
            <div v-for="(m, i) in mpStatusLegend" :key="i" class="mp-leg-row">
              <span class="mp-leg-dot" :style="`background:${m.color}`"></span>
              <span class="mp-leg-lbl">{{ m.label }}</span>
              <strong class="mp-leg-val">{{ m.value }}</strong>
            </div>
          </div>
        </div>
      </div>

      <!-- ══ ROW 2.5: GEO MAP ══ -->
      <div class="dash-row">
        <geo-map-dashboard
          :student-data="filteredStudentData"
          :manpower-data="filteredManpowerData"
          :filter-year="filterYear"
          style="flex:1;min-width:0"
          @filter-province="onProvinceFilter"
        />
      </div>

      <!-- ══ ROW 3: REGION TABLE + TREND + MANPOWER ══ -->
      <div class="dash-row">
        <!-- 3A: Region Table -->
        <div class="panel" style="flex:1.5">
          <div class="panel-hd">
            <v-icon size="16" color="#0891b2" class="mr-2"
              >mdi-map-legend</v-icon
            >
            <span>บทสรุปนักเรียนรายภาค</span>
            <v-spacer />
            <v-chip x-small outlined color="#0891b2"
              >{{ filterYear }}/{{ filterSemester }}</v-chip
            >
          </div>
          <div class="tbl-wrap">
            <table class="reg-tbl">
              <thead>
                <tr>
                  <th>ภาค</th>
                  <th class="ta-r">นักเรียน</th>
                  <th class="ta-r">ปวช.</th>
                  <th class="ta-r">ปวส.</th>
                  <th class="ta-r">วิทยาลัย</th>
                  <th style="width:90px">สัดส่วน</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="r in regionSummary"
                  :key="r.region_name"
                  class="reg-tr"
                  :class="{ 'reg-tr--active': filterRegion == r.region_ID }"
                  @click="toggleRegion(r)"
                >
                  <td>
                    <span class="rdot" :style="`background:${r.color}`"></span
                    >{{ r.region_name }}
                  </td>
                  <td class="ta-r fw6">{{ formatNum(r.students) }}</td>
                  <td class="ta-r c-blue">{{ formatNum(r.ch) }}</td>
                  <td class="ta-r c-purple">{{ formatNum(r.hs) }}</td>
                  <td class="ta-r">{{ r.colleges }}</td>
                  <td>
                    <div class="rbar-bg">
                      <div
                        class="rbar-fill"
                        :style="`width:${r.pct}%;background:${r.color}`"
                      ></div>
                    </div>
                    <span class="rpct">{{ r.pct }}%</span>
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr class="reg-total">
                  <td>รวม</td>
                  <td class="ta-r fw6">{{ formatNum(totalStudents) }}</td>
                  <td class="ta-r c-blue">{{ formatNum(totalCh) }}</td>
                  <td class="ta-r c-purple">{{ formatNum(totalHs) }}</td>
                  <td class="ta-r">{{ totalColleges }}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <!-- 3B: Trend -->
        <div class="panel" style="flex:2">
          <div class="panel-hd">
            <v-icon size="16" color="#0891b2" class="mr-2"
              >mdi-chart-line</v-icon
            >
            <span>แนวโน้มนักเรียน 3 ปี + พยากรณ์</span>
            <v-spacer />
            <v-btn-toggle
              v-model="trendMode"
              mandatory
              dense
              class="mode-toggle"
            >
              <v-btn x-small value="total">รวม</v-btn>
              <v-btn x-small value="split">แยกระดับ</v-btn>
            </v-btn-toggle>
          </div>
          <apexchart
            v-if="trendChart.series.length"
            type="area"
            height="230"
            :options="trendChart.opts"
            :series="trendChart.series"
          />
          <div v-else class="no-data">ไม่มีข้อมูล</div>
          <div class="insight-bar" v-if="trendInsight">
            <v-icon size="13" :color="trendInsight.color" class="mr-1">{{
              trendInsight.icon
            }}</v-icon>
            <span>{{ trendInsight.text }}</span>
          </div>
        </div>

        <!-- 3C: Manpower Breakdown (กรองตาม scope เดียวกัน) -->
        <div class="panel" style="flex:1">
          <div class="panel-hd">
            <v-icon size="16" color="#d97706" class="mr-2"
              >mdi-account-supervisor</v-icon
            >
            <span>อัตรากำลังบุคลากร</span>
            <v-spacer />
            <v-chip x-small outlined color="#d97706">{{ filterYear }}</v-chip>
          </div>
          <div class="mp-breakdown">
            <div
              v-for="mp in manpowerBreakdown"
              :key="mp.key"
              class="mp-bk-row"
            >
              <div class="mp-bk-left">
                <v-icon :color="mp.color" size="16" class="mr-2">{{
                  mp.icon
                }}</v-icon>
                <span class="mp-bk-lbl">{{ mp.label }}</span>
              </div>
              <div class="mp-bk-right">
                <span class="mp-bk-val">{{ formatNum(mp.value) }}</span>
                <div class="mp-bk-bar-bg">
                  <div
                    class="mp-bk-bar-fill"
                    :style="`width:${mp.pct}%;background:${mp.color}`"
                  ></div>
                </div>
              </div>
            </div>
            <div class="criterion-box">
              <div class="criterion-row">
                <span>เกณฑ์รวม</span>
                <strong>{{ formatNum(filteredCriterionAll) }}</strong>
              </div>
              <div class="criterion-row">
                <span>ขาด/เกิน (รวม)</span>
                <strong
                  :class="filteredCriterionIncomplete < 0 ? 'c-red' : 'c-green'"
                >
                  {{ filteredCriterionIncomplete < 0 ? "▼" : "▲" }}
                  {{ formatNum(Math.abs(filteredCriterionIncomplete)) }}
                </strong>
              </div>
              <div class="criterion-row">
                <span>อัตราส่วนครู:นร.</span>
                <strong class="c-blue">1 : {{ teacherRatio }}</strong>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ══ ROW 3.5: TREND DECLINE ══ -->
      <div class="dash-row">
        <trend-decline-panel
          :data2566="studentData2566"
          :data2567="studentData2567"
          :data2568="studentData2568"
          :risk-threshold="200"
          style="flex:1;min-width:0"
        />
      </div>

      <!-- ══ ROW 3.7: BRANCH DENSITY MAP ══ -->
      <div class="dash-row">
        <branch-density-map
          :student-data="filteredStudentData"
          :year="filterYear"
          style="flex:1;min-width:0"
        />
      </div>

      <!-- ══ ROW 4: TOP SUBJECTS + BRANCH ANALYSIS ══ -->
      <div class="dash-row">
        <!-- 4A: Top Subject Types chart -->
        <div class="panel" style="flex:1.8">
          <div class="panel-hd">
            <v-icon size="16" color="#059669" class="mr-2"
              >mdi-book-open-variant</v-icon
            >
            <span>ประเภทวิชา — จำนวนนักเรียน (Top 10)</span>
            <v-spacer />
            <div class="tab-grp">
              <span
                v-for="t in ['ปวช.', 'ปวส.', 'รวม']"
                :key="t"
                :class="['tab-btn', { active: subjectMode === t }]"
                @click="subjectMode = t"
                >{{ t }}</span
              >
            </div>
          </div>
          <apexchart
            v-if="subjectChart.series.length"
            type="bar"
            height="250"
            :options="subjectChart.opts"
            :series="subjectChart.series"
          />
          <div v-else class="no-data">ไม่มีข้อมูล</div>
        </div>

        <!-- 4B: Branch Insight — มากสุด / น้อยสุด / อัตราครู -->
        <div class="panel" style="flex:2">
          <div class="panel-hd">
            <v-icon size="16" color="#dc2626" class="mr-2">mdi-podium</v-icon>
            <span>วิเคราะห์สาขาวิชา</span>
            <v-spacer />
            <div class="tab-grp">
              <span
                v-for="t in ['มากสุด', 'น้อยสุด', 'ครู:นร.']"
                :key="t"
                :class="['tab-btn', { active: branchInsightMode === t }]"
                @click="branchInsightMode = t"
                >{{ t }}</span
              >
            </div>
          </div>

          <!-- Top 10 มากสุด -->
          <div v-if="branchInsightMode === 'มากสุด'" class="insight-list">
            <div v-for="(b, i) in topBranches" :key="i" class="insight-row">
              <span
                class="insight-rank"
                :class="i < 3 ? 'rank-gold' : 'rank-normal'"
                >{{ i + 1 }}</span
              >
              <div class="insight-info">
                <div class="insight-name">{{ b.name }}</div>
                <div class="insight-sub">{{ b.subjectType }}</div>
              </div>
              <div class="insight-bars">
                <div class="ibar-row">
                  <span class="ibar-lbl c-blue">ปวช.</span>
                  <div class="ibar-bg">
                    <div
                      class="ibar-fill ibar-fill--blue"
                      :style="
                        `width:${
                          topBranchMax ? (b.ch / topBranchMax) * 100 : 0
                        }%`
                      "
                    ></div>
                  </div>
                  <span class="ibar-val">{{ formatNum(b.ch) }}</span>
                </div>
                <div class="ibar-row">
                  <span class="ibar-lbl c-purple">ปวส.</span>
                  <div class="ibar-bg">
                    <div
                      class="ibar-fill ibar-fill--purple"
                      :style="
                        `width:${
                          topBranchMax ? (b.hs / topBranchMax) * 100 : 0
                        }%`
                      "
                    ></div>
                  </div>
                  <span class="ibar-val">{{ formatNum(b.hs) }}</span>
                </div>
              </div>
              <div class="insight-total fw6">{{ formatNum(b.total) }}</div>
            </div>
          </div>

          <!-- Bottom 10 น้อยสุด -->
          <div v-else-if="branchInsightMode === 'น้อยสุด'" class="insight-list">
            <div
              v-for="(b, i) in bottomBranches"
              :key="i"
              class="insight-row insight-row--dim"
            >
              <span class="insight-rank rank-red">{{ i + 1 }}</span>
              <div class="insight-info">
                <div class="insight-name">{{ b.name }}</div>
                <div class="insight-sub">
                  {{ b.subjectType }} · {{ b.colleges }} วิทยาลัย
                </div>
              </div>
              <div class="insight-total c-red">{{ formatNum(b.total) }} คน</div>
            </div>
          </div>

          <!-- อัตราส่วนครู:นักเรียน รายสาขา -->
          <div v-else class="insight-list">
            <div class="insight-caption">
              <v-icon size="13" color="#6b7280">mdi-information-outline</v-icon>
              คำนวณจากสัดส่วนนักเรียนต่อจำนวนครูของสถานศึกษาที่มีสาขานั้น
            </div>
            <div
              v-for="(b, i) in branchTeacherRatio"
              :key="i"
              class="insight-row"
            >
              <span class="insight-rank rank-normal">{{ i + 1 }}</span>
              <div class="insight-info">
                <div class="insight-name">{{ b.name }}</div>
                <div class="insight-sub">{{ b.subjectType }}</div>
              </div>
              <div
                class="ratio-badge"
                :class="
                  b.ratio > 25
                    ? 'ratio-red'
                    : b.ratio > 15
                    ? 'ratio-amber'
                    : 'ratio-green'
                "
              >
                1:{{ b.ratio }}
              </div>
            </div>
            <div
              v-if="!branchTeacherRatio.length"
              class="no-data"
              style="height:80px"
            >
              ต้องการข้อมูลอัตรากำลังจาก manpowerData
            </div>
          </div>
        </div>
      </div>

      <!-- ══ ROW 5: BRANCH TABLE (v-data-table) ══ -->
      <div class="dash-row">
        <div class="panel" style="flex:1">
          <div class="panel-hd">
            <v-icon size="16" color="#7c3aed" class="mr-2">mdi-magnify</v-icon>
            <span>ค้นหาสาขาวิชา</span>
            <v-chip x-small outlined color="#7c3aed" class="ml-2"
              >{{ filteredBranches.length }} สาขา</v-chip
            >
            <v-spacer />
            <v-text-field
              v-model="searchBranch"
              dense
              outlined
              hide-details
              clearable
              placeholder="พิมพ์ชื่อสาขาวิชา..."
              class="branch-search"
            />
            <v-btn x-small class="btn-export ml-2" @click="exportBranchXlsx">
              <v-icon size="13" left>mdi-microsoft-excel</v-icon>ส่งออก
            </v-btn>
          </div>
          <v-data-table
            :headers="branchHeaders"
            :items="filteredBranches"
            :items-per-page="15"
            :footer-props="{
              itemsPerPageOptions: [10, 15, 25, 50, -1],
              itemsPerPageText: 'แถว/หน้า'
            }"
            dense
            class="branch-dt"
            :sort-by.sync="branchSortBy"
            :sort-desc.sync="branchSortDesc"
          >
            <template #[`item.name`]="{ item }">
              <span class="branch-name-cell">{{ item.name }}</span>
            </template>
            <template #[`item.subjectType`]="{ item }">
              <v-chip x-small outlined color="#6366f1">{{
                item.subjectType
              }}</v-chip>
            </template>
            <template #[`item.ch`]="{ item }">
              <span class="c-blue fw5">{{ formatNum(item.ch) }}</span>
            </template>
            <template #[`item.hs`]="{ item }">
              <span class="c-purple fw5">{{ formatNum(item.hs) }}</span>
            </template>
            <template #[`item.total`]="{ item }">
              <strong>{{ formatNum(item.total) }}</strong>
            </template>
            <template #[`item.colleges`]="{ item }">
              <span class="college-badge">{{ item.colleges }}</span> แห่ง
            </template>
            <template #[`item.pctOfTotal`]="{ item }">
              <div class="pct-bar-wrap">
                <div
                  class="pct-bar-fill"
                  :style="`width:${item.pctOfTotal}%`"
                ></div>
                <span class="pct-val">{{ item.pctOfTotal }}%</span>
              </div>
            </template>
          </v-data-table>
        </div>
      </div>

      <!-- ══ ROW 6: COLLEGE TABLE (v-data-table) ══ -->
      <div class="dash-row">
        <div class="panel" style="flex:1">
          <div class="panel-hd">
            <v-icon size="16" color="#0891b2" class="mr-2"
              >mdi-office-building-outline</v-icon
            >
            <span>ข้อมูลสถานศึกษา</span>
            <v-chip x-small outlined color="#0891b2" class="ml-2"
              >{{ filteredCollegeRows.length }} แห่ง</v-chip
            >
            <v-spacer />
            <v-text-field
              v-model="searchCollege"
              dense
              outlined
              hide-details
              clearable
              placeholder="ค้นหาสถานศึกษา..."
              prepend-inner-icon="mdi-magnify"
              class="branch-search"
            />
            <v-btn x-small class="btn-export ml-2" @click="exportCollegeXlsx">
              <v-icon size="13" left>mdi-microsoft-excel</v-icon>ส่งออก
            </v-btn>
          </div>
          <v-data-table
            :headers="collegeHeaders"
            :items="filteredCollegeRows"
            :items-per-page="20"
            :footer-props="{
              itemsPerPageOptions: [10, 20, 50, -1],
              itemsPerPageText: 'แถว/หน้า'
            }"
            dense
            class="college-dt"
            :sort-by.sync="collegeSortBy"
            :sort-desc.sync="collegeSortDesc"
          >
            <template #[`item.code`]="{ item }">
              <span class="mono-sm">{{ item.code }}</span>
            </template>
            <template #[`item.name`]="{ item }">
              <span class="college-nm">{{ item.name }}</span>
            </template>
            <template #[`item.region`]="{ item }">
              <span
                class="region-tag"
                :style="`background:${regionTagColor(item.region)}`"
                >{{ item.region }}</span
              >
            </template>
            <template #[`item.ch`]="{ item }">
              <span class="c-blue fw5">{{ formatNum(item.ch) }}</span>
            </template>
            <template #[`item.hs`]="{ item }">
              <span class="c-purple fw5">{{ formatNum(item.hs) }}</span>
            </template>
            <template #[`item.total`]="{ item }">
              <strong>{{ formatNum(item.total) }}</strong>
            </template>
            <template #[`item.incomplete`]="{ item }">
              <span
                :class="
                  item.incomplete < 0
                    ? 'badge-red'
                    : item.incomplete > 0
                    ? 'badge-green'
                    : 'badge-gray'
                "
              >
                {{ item.incomplete < 0 ? "▼" : item.incomplete > 0 ? "▲" : "="
                }}{{ formatNum(Math.abs(item.incomplete)) }}
              </span>
            </template>
          </v-data-table>
        </div>
      </div>

      <!-- ══ ROW 7: CHALLENGES + TRANSFORMATIONS + ALERTS ══ -->
      <div class="dash-row">
        <div class="panel" style="flex:1">
          <div class="panel-hd">
            <v-icon size="16" color="#dc2626" class="mr-2"
              >mdi-alert-decagram</v-icon
            >
            <span>5 Big Challenges</span>
          </div>
          <div class="list-body">
            <div
              v-for="(c, i) in bigChallenges"
              :key="i"
              class="list-item list-item--red"
            >
              <span class="list-num list-num--red">{{ i + 1 }}</span>
              <span>{{ c }}</span>
            </div>
          </div>
        </div>
        <div class="panel" style="flex:1">
          <div class="panel-hd">
            <v-icon size="16" color="#059669" class="mr-2"
              >mdi-lightning-bolt</v-icon
            >
            <span>5 Transformations</span>
          </div>
          <div class="list-body">
            <div
              v-for="(t, i) in transformations"
              :key="i"
              class="list-item list-item--green"
            >
              <v-icon :color="t.color" size="16" class="mr-2">{{
                t.icon
              }}</v-icon>
              <div>
                <div class="list-title">{{ t.title }}</div>
                <div class="list-desc">{{ t.desc }}</div>
              </div>
            </div>
          </div>
        </div>
        <div class="panel" style="flex:1.3">
          <div class="panel-hd">
            <v-icon size="16" color="#d97706" class="mr-2"
              >mdi-bell-ring</v-icon
            >
            <span>การแจ้งเตือน</span>
            <v-chip x-small outlined color="#d97706" class="ml-2">{{
              alerts.length
            }}</v-chip>
          </div>
          <div class="alert-body">
            <div
              v-for="(a, i) in alerts"
              :key="i"
              class="alert-item"
              :class="`alert-item--${a.type}`"
            >
              <v-icon size="16" :color="alertColor(a.type)" class="mr-2">{{
                alertIcon(a.type)
              }}</v-icon>
              <div>
                <div class="alert-title">{{ a.title }}</div>
                <div class="alert-desc">{{ a.desc }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- FOOTER -->
      <div class="dash-footer">
        <span
          >ข้อมูล ณ วันที่ {{ today }} &nbsp;|&nbsp; ที่มา: ระบบ ศธ.02/PVRS, VEC
          Data Catalog (ckan.vec.go.th)</span
        >
        <span>สอศ. © {{ currentYear }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import GeoMapDashboard from "./GeoMapDashboard.vue";
import TrendDeclinePanel from "./TrendDeclinePanel.vue";
import BranchDensityMap from "./BranchDensityMap.vue";

const API_KEY = "HRvec2021";
const REGION_COLORS = {
  ภาคกลาง: "#2563eb",
  ภาคเหนือ: "#059669",
  ภาคตะวันออกเฉียงเหนือ: "#d97706",
  ภาคใต้: "#7c3aed",
  ภาคตะวันออก: "#0891b2",
  ภาคตะวันตก: "#db2777",
  กรุงเทพมหานคร: "#4f46e5"
};
const REGION_TAGS = {
  ภาคกลาง: "#dbeafe",
  ภาคเหนือ: "#d1fae5",
  ภาคตะวันออกเฉียงเหนือ: "#fef3c7",
  ภาคใต้: "#ede9fe",
  ภาคตะวันออก: "#cffafe",
  ภาคตะวันตก: "#fce7f3",
  กรุงเทพมหานคร: "#e0e7ff"
};

function linearForecast(data) {
  const n = data.length;
  if (n < 2) return [];
  const xs = data.map((_, i) => i),
    ys = data;
  const sumX = xs.reduce((a, b) => a + b, 0),
    sumY = ys.reduce((a, b) => a + b, 0);
  const sumXY = xs.reduce((acc, x, i) => acc + x * ys[i], 0),
    sumXX = xs.reduce((acc, x) => acc + x * x, 0);
  const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;
  return [1, 2, 3].map(i => Math.round(intercept + slope * (n - 1 + i)));
}

export default {
  name: "ExecutiveDashboard",
  components: { GeoMapDashboard, TrendDeclinePanel, BranchDensityMap },

  data() {
    return {
      loading: false,
      drawerOpen: false,
      today: new Date().toLocaleDateString("th-TH", {
        year: "numeric",
        month: "long",
        day: "numeric"
      }),
      currentYear: new Date().getFullYear() + 543,
      filterYear: "2568",
      filterSemester: "1",
      filterRegion: null,
      filterProvince: null,
      filterCollege: null,
      searchBranch: "",
      searchCollege: "",
      // v-data-table sort state
      branchSortBy: "total",
      branchSortDesc: true,
      collegeSortBy: "total",
      collegeSortDesc: true,
      yearOptions: ["2568", "2567", "2566", "2565", "2564"],
      semesterOptions: [
        { text: "ภาคเรียนที่ 1", value: "1" },
        { text: "ภาคเรียนที่ 2", value: "2" }
      ],
      regionOptions: [],
      provinceOptions: [],
      studentData: [],
      studentDataPrev: [],
      studentDataPrev2: [],
      manpowerData: [],
      studentData2566: [],
      studentData2567: [],
      studentData2568: [],
      levelMode: "ภาค",
      trendMode: "split",
      subjectMode: "รวม",
      branchInsightMode: "มากสุด",
      levelChart: { series: [], opts: {} },
      trendChart: { series: [], opts: {} },
      subjectChart: { series: [], opts: {} },
      mpDonut: { series: [], opts: {} },
      bigChallenges: [
        "นักเรียนลดลงต่อเนื่องในหลายพื้นที่",
        "โลกของงานเปลี่ยนแปลง เทคโนโลยีก้าวโดด",
        "ครูไม่มีทักษะใหม่ และทักษะแห่งอนาคต",
        "หลักสูตรไม่ยืดหยุ่น ไม่ตอบสนองความต้องการพื้นที่",
        "งบประมาณจำกัด ภาระค่าใช้จ่ายเพิ่มขึ้น"
      ],
      transformations: [
        {
          title: "Flexible Education",
          desc: "การศึกษายืดหยุ่น ตอบโจทย์ผู้เรียน",
          icon: "mdi-school",
          color: "#2563eb"
        },
        {
          title: "Digital TVET",
          desc: "อาชีวศึกษาดิจิทัลครบวงจร",
          icon: "mdi-monitor-dashboard",
          color: "#7c3aed"
        },
        {
          title: "Industry-Driven TVET",
          desc: "ขับเคลื่อนด้วยภาคอุตสาหกรรม",
          icon: "mdi-factory",
          color: "#059669"
        },
        {
          title: "Future Skill Curriculum",
          desc: "หลักสูตรทักษะแห่งอนาคต",
          icon: "mdi-brain",
          color: "#d97706"
        },
        {
          title: "Data-Driven Management",
          desc: "บริหารด้วยข้อมูลสารสนเทศ",
          icon: "mdi-database-eye",
          color: "#dc2626"
        }
      ]
    };
  },

  computed: {
    // ── กรอง studentData ตาม filterRegion / filterProvince / filterCollege ──
    filteredStudentData() {
      let d = [...this.studentData];
      if (this.filterRegion)
        d = d.filter(r => r.region_ID == this.filterRegion);
      if (this.filterProvince)
        d = d.filter(r => r.province_ID == this.filterProvince);
      if (this.filterCollege)
        d = d.filter(r => r.rate_work_college_code == this.filterCollege);
      return d;
    },
    // ── กรอง manpowerData ให้สอดคล้องกับ filterCollege/Province/Region ──
    // (manpowerData มี province_name จาก rate_work_g.php JOIN province)
    filteredManpowerData() {
      let d = [...this.manpowerData];
      if (this.filterCollege) {
        d = d.filter(
          r =>
            (r.rate_work_g_college_code || r.college_code) ===
            this.filterCollege
        );
      } else if (this.filterProvince) {
        const pName = (
          this.provinceOptions.find(
            p => p.province_ID == this.filterProvince
          ) || {}
        ).province_name;
        if (pName) d = d.filter(r => r.province_name === pName);
      } else if (this.filterRegion) {
        // map region_ID → ชื่อจังหวัดในภาคนั้น แล้วกรอง
        const pvIds = this.provinceOptions
          .filter(p => p.region_ID == this.filterRegion)
          .map(p => p.province_name);
        if (pvIds.length) d = d.filter(r => pvIds.includes(r.province_name));
      }
      return d;
    },
    filteredProvinces() {
      if (!this.filterRegion) return this.provinceOptions;
      return this.provinceOptions.filter(p => p.region_ID == this.filterRegion);
    },
    // collegeOptions: กรองตาม filterRegion และ filterProvince เพื่อให้ dropdown สอดคล้อง
    collegeOptions() {
      let src = this.studentData;
      if (this.filterRegion)
        src = src.filter(r => r.region_ID == this.filterRegion);
      if (this.filterProvince)
        src = src.filter(r => r.province_ID == this.filterProvince);
      const map = {};
      src.forEach(r => {
        if (r.rate_work_college_code && r.college_name)
          map[r.rate_work_college_code] = r.college_name;
      });
      return Object.entries(map).map(([k, v]) => ({
        college_code: k,
        college_name: v
      }));
    },

    // ── KPI ──
    totalStudents() {
      return this.filteredStudentData.reduce(
        (s, r) =>
          s +
          (+r.rate_work_field_study_lavel_1 || 0) +
          (+r.rate_work_field_study_lavel_2 || 0) +
          (+r.rate_work_field_study_lavel_3 || 0),
        0
      );
    },
    totalCh() {
      return this.filteredStudentData
        .filter(r => r.rate_work_college_id_class == 20)
        .reduce(
          (s, r) =>
            s +
            (+r.rate_work_field_study_lavel_1 || 0) +
            (+r.rate_work_field_study_lavel_2 || 0) +
            (+r.rate_work_field_study_lavel_3 || 0),
          0
        );
    },
    totalHs() {
      return this.filteredStudentData
        .filter(r => r.rate_work_college_id_class == 30)
        .reduce(
          (s, r) =>
            s +
            (+r.rate_work_field_study_lavel_1 || 0) +
            (+r.rate_work_field_study_lavel_2 || 0) +
            (+r.rate_work_field_study_lavel_3 || 0),
          0
        );
    },
    totalColleges() {
      return new Set(
        this.filteredStudentData.map(r => r.rate_work_college_code)
      ).size;
    },
    totalBranches() {
      return new Set(
        this.filteredStudentData
          .map(r => r.rate_id_course_branch)
          .filter(Boolean)
      ).size;
    },

    // ── อัตรากำลัง: ใช้ filteredManpowerData เสมอ เพื่อให้ตรงกับ scope ที่กรอง ──
    filteredTeachOfficial() {
      return this.filteredManpowerData.reduce(
        (s, r) => s + (+r.rate_teach_official_all || 0),
        0
      );
    },
    filteredTeachCivil() {
      return this.filteredManpowerData.reduce(
        (s, r) => s + (+r.rate_teach_civil_all || 0),
        0
      );
    },
    filteredManpowerAll() {
      return this.filteredManpowerData.reduce(
        (s, r) => s + (+r.rate_man_power_all || 0),
        0
      );
    },
    filteredCriterionAll() {
      return this.filteredManpowerData.reduce(
        (s, r) => s + (+r.rate_criterion_all || 0),
        0
      );
    },
    filteredCriterionIncomplete() {
      return this.filteredManpowerData.reduce(
        (s, r) => s + (+r.rate_criterion_incomplete_all || 0),
        0
      );
    },
    // อัตราส่วนครู:นักเรียน — คำนวณจาก scope เดียวกัน
    teacherRatio() {
      if (!this.filteredTeachOfficial) return "-";
      return (this.totalStudents / this.filteredTeachOfficial).toFixed(1);
    },

    trendPct() {
      const prev = this.calcTotal(this.studentDataPrev);
      if (!prev) return null;
      return Math.round(((this.totalStudents - prev) / prev) * 100);
    },

    donutOpts() {
      return {
        chart: { background: "transparent", foreColor: "#374151" },
        labels: ["ปวช.", "ปวส."],
        colors: ["#2563eb", "#7c3aed"],
        legend: { show: false },
        dataLabels: {
          enabled: true,
          formatter: v => v.toFixed(1) + "%",
          style: {
            fontSize: "12px",
            fontFamily: "Sarabun,sans-serif",
            colors: ["#fff"]
          }
        },
        plotOptions: { pie: { donut: { size: "62%" } } },
        stroke: { width: 0 },
        tooltip: { y: { formatter: v => this.formatNum(v) + " คน" } }
      };
    },

    regionSummary() {
      const map = {};
      this.filteredStudentData.forEach(r => {
        const name = r.region_name || "ไม่ระบุ",
          id = r.region_ID;
        if (!map[name])
          map[name] = {
            region_name: name,
            region_ID: id,
            students: 0,
            ch: 0,
            hs: 0,
            colleges: new Set()
          };
        const t =
          (+r.rate_work_field_study_lavel_1 || 0) +
          (+r.rate_work_field_study_lavel_2 || 0) +
          (+r.rate_work_field_study_lavel_3 || 0);
        map[name].students += t;
        if (r.rate_work_college_id_class == 20) map[name].ch += t;
        if (r.rate_work_college_id_class == 30) map[name].hs += t;
        map[name].colleges.add(r.rate_work_college_code);
      });
      const total = this.totalStudents || 1;
      return Object.values(map)
        .map(r => ({
          ...r,
          colleges: r.colleges.size,
          pct: Math.round((r.students / total) * 100),
          color: REGION_COLORS[r.region_name] || "#6b7280"
        }))
        .sort((a, b) => b.students - a.students);
    },

    manpowerBreakdown() {
      const total = this.filteredManpowerAll || 1;
      return [
        {
          key: "official",
          label: "ข้าราชการครู",
          value: this.filteredTeachOfficial,
          icon: "mdi-human-male-board",
          color: "#2563eb",
          pct: Math.round((this.filteredTeachOfficial / total) * 100)
        },
        {
          key: "civil",
          label: "พนักงานราชการ",
          value: this.filteredTeachCivil,
          icon: "mdi-account-star",
          color: "#7c3aed",
          pct: Math.round((this.filteredTeachCivil / total) * 100)
        },
        {
          key: "total",
          label: "อัตรากำลังรวม",
          value: this.filteredManpowerAll,
          icon: "mdi-badge-account",
          color: "#059669",
          pct: 100
        }
      ];
    },

    mpStatusLegend() {
      const confirmed = this.filteredManpowerData.filter(
        r => r.rate_work_g_status === "Confirm"
      ).length;
      const inprogress = this.filteredManpowerData.filter(
        r => r.rate_work_g_status === "Save"
      ).length;
      const pending = this.filteredManpowerData.length - confirmed - inprogress;
      return [
        { label: "เรียบร้อย (Confirm)", value: confirmed, color: "#059669" },
        { label: "กำลังดำเนิน (Save)", value: inprogress, color: "#d97706" },
        { label: "ยังไม่ดำเนิน", value: pending, color: "#dc2626" }
      ];
    },

    // ── Branch computed ──
    filteredBranches() {
      const map = {};
      this.filteredStudentData.forEach(r => {
        const k = r.name_type_course || r.rate_id_course_branch || "ไม่ระบุ";
        if (!map[k])
          map[k] = {
            name: k,
            subjectType: r.subject_typeName || "",
            ch: 0,
            hs: 0,
            total: 0,
            colleges: new Set()
          };
        const v =
          (+r.rate_work_field_study_lavel_1 || 0) +
          (+r.rate_work_field_study_lavel_2 || 0) +
          (+r.rate_work_field_study_lavel_3 || 0);
        if (r.rate_work_college_id_class == 20) map[k].ch += v;
        else if (r.rate_work_college_id_class == 30) map[k].hs += v;
        map[k].total += v;
        map[k].colleges.add(r.rate_work_college_code);
      });
      const totalAll = this.totalStudents || 1;
      let arr = Object.values(map).map(b => ({
        ...b,
        colleges: b.colleges.size,
        pctOfTotal: Math.round((b.total / totalAll) * 100)
      }));
      if (this.searchBranch) {
        const q = this.searchBranch.toLowerCase();
        arr = arr.filter(
          b =>
            b.name.toLowerCase().includes(q) ||
            b.subjectType.toLowerCase().includes(q)
        );
      }
      return arr;
    },

    topBranches() {
      return [...this.filteredBranches]
        .sort((a, b) => b.total - a.total)
        .slice(0, 10);
    },
    topBranchMax() {
      return this.topBranches.length
        ? Math.max(...this.topBranches.map(b => Math.max(b.ch, b.hs)))
        : 1;
    },
    bottomBranches() {
      return [...this.filteredBranches]
        .filter(b => b.total > 0)
        .sort((a, b) => a.total - b.total)
        .slice(0, 10);
    },

    // อัตราส่วนครู:นักเรียนต่อสาขา — ใช้ manpower ระดับวิทยาลัย → แจกสัดส่วน
    branchTeacherRatio() {
      if (!this.filteredManpowerData.length) return [];
      // สร้าง map college_code → manpower (ข้าราชการครู)
      const mpMap = {};
      this.filteredManpowerData.forEach(r => {
        const code = r.rate_work_g_college_code || r.college_code;
        if (code)
          mpMap[code] =
            (+r.rate_teach_official_all || 0) + (+r.rate_teach_civil_all || 0);
      });
      // สำหรับแต่ละสาขา นับนักเรียนรวม + นับ teacher จากวิทยาลัยที่มีสาขานั้น
      const map = {};
      this.filteredStudentData.forEach(r => {
        const k = r.name_type_course || r.rate_id_course_branch || "ไม่ระบุ";
        if (!map[k])
          map[k] = {
            name: k,
            subjectType: r.subject_typeName || "",
            students: 0,
            teachers: 0,
            colleges: new Set()
          };
        const v =
          (+r.rate_work_field_study_lavel_1 || 0) +
          (+r.rate_work_field_study_lavel_2 || 0) +
          (+r.rate_work_field_study_lavel_3 || 0);
        map[k].students += v;
        if (!map[k].colleges.has(r.rate_work_college_code)) {
          map[k].colleges.add(r.rate_work_college_code);
          map[k].teachers += mpMap[r.rate_work_college_code] || 0;
        }
      });
      return Object.values(map)
        .filter(b => b.teachers > 0)
        .map(b => ({ ...b, ratio: +(b.students / b.teachers).toFixed(1) }))
        .sort((a, b) => b.ratio - a.ratio)
        .slice(0, 15);
    },

    filteredCollegeRows() {
      const studentMap = {};
      this.filteredStudentData.forEach(r => {
        const code = r.rate_work_college_code;
        if (!studentMap[code])
          studentMap[code] = {
            code,
            name: r.college_name || code,
            province: r.province_name || "",
            region: r.region_name || "",
            ch: 0,
            hs: 0,
            total: 0,
            branches: new Set()
          };
        const v =
          (+r.rate_work_field_study_lavel_1 || 0) +
          (+r.rate_work_field_study_lavel_2 || 0) +
          (+r.rate_work_field_study_lavel_3 || 0);
        if (r.rate_work_college_id_class == 20) studentMap[code].ch += v;
        else if (r.rate_work_college_id_class == 30) studentMap[code].hs += v;
        studentMap[code].total += v;
        if (r.rate_id_course_branch)
          studentMap[code].branches.add(r.rate_id_course_branch);
      });
      const mpMap = {};
      this.filteredManpowerData.forEach(r => {
        const code = r.rate_work_g_college_code || r.college_code;
        if (code)
          mpMap[code] = {
            manpower: +r.rate_man_power_all || 0,
            criterion: +r.rate_criterion_all || 0,
            incomplete: +r.rate_criterion_incomplete_all || 0
          };
      });
      let arr = Object.values(studentMap).map(c => ({
        ...c,
        branches: c.branches.size,
        ...(mpMap[c.code] || { manpower: 0, criterion: 0, incomplete: 0 })
      }));
      if (this.searchCollege) {
        const q = this.searchCollege.toLowerCase();
        arr = arr.filter(
          c =>
            c.name.toLowerCase().includes(q) ||
            c.code.toLowerCase().includes(q) ||
            c.province.toLowerCase().includes(q)
        );
      }
      return arr;
    },

    trendInsight() {
      const t0 = this.calcTotal(this.studentDataPrev2),
        t1 = this.calcTotal(this.studentDataPrev),
        t2 = this.totalStudents;
      if (!t0 || !t1) return null;
      const change = (((t2 - t0) / t0) * 100).toFixed(1);
      return {
        text: `3 ปี: ${change >= 0 ? "+" : ""}${change}% (${this.formatNum(
          t2 - t0
        )} คน)`,
        color: change >= 0 ? "#059669" : "#dc2626",
        icon: change >= 0 ? "mdi-trending-up" : "mdi-trending-down"
      };
    },

    alerts() {
      const list = [];
      if (this.trendPct !== null && this.trendPct < -5)
        list.push({
          type: "warning",
          title: "นักเรียนลดลง",
          desc: `ลดลง ${Math.abs(this.trendPct)}% เทียบปีก่อนหน้า`
        });
      if (this.filteredCriterionIncomplete < -100)
        list.push({
          type: "error",
          title: "อัตรากำลังต่ำกว่าเกณฑ์",
          desc: `ขาดอีก ${this.formatNum(
            Math.abs(this.filteredCriterionIncomplete)
          )} อัตรา`
        });
      if (this.teacherRatio !== "-" && +this.teacherRatio > 20)
        list.push({
          type: "error",
          title: "อัตราส่วนครู:นร. เกินมาตรฐาน",
          desc: `ปัจจุบัน 1:${this.teacherRatio} (มาตรฐาน 1:20)`
        });
      const pending = this.filteredManpowerData.filter(
        r =>
          r.rate_work_g_status !== "Confirm" && r.rate_work_g_status !== "Save"
      ).length;
      if (pending > 0)
        list.push({
          type: "warning",
          title: "สถานศึกษายังไม่รายงานอัตรากำลัง",
          desc: `${pending} แห่ง ยังไม่ดำเนินการ`
        });
      list.push({
        type: "success",
        title: "ข้อมูลอัปเดตแล้ว",
        desc: `ข้อมูลล่าสุด: ${this.today}`
      });
      return list;
    },

    // ── v-data-table headers ──
    branchHeaders() {
      return [
        { text: "สาขาวิชา", value: "name", width: "220px" },
        { text: "ประเภทวิชา", value: "subjectType", width: "140px" },
        { text: "ปวช.", value: "ch", align: "right", width: "80px" },
        { text: "ปวส.", value: "hs", align: "right", width: "80px" },
        { text: "รวม", value: "total", align: "right", width: "90px" },
        { text: "วิทยาลัย", value: "colleges", align: "right", width: "80px" },
        {
          text: "% ของรวม",
          value: "pctOfTotal",
          align: "right",
          width: "120px"
        }
      ];
    },
    collegeHeaders() {
      return [      
        { text: "ชื่อสถานศึกษา", value: "name", width: "220px" },
        { text: "จังหวัด", value: "province", width: "100px" },
        { text: "ภาค", value: "region", width: "100px" },
        { text: "ปวช.", value: "ch", align: "right", width: "70px" },
        { text: "ปวส.", value: "hs", align: "right", width: "70px" },
        { text: "รวม", value: "total", align: "right", width: "80px" },
        { text: "สาขา", value: "branches", align: "right", width: "60px" },
        {
          text: "อัตรากำลัง",
          value: "manpower",
          align: "right",
          width: "90px"
        },
        { text: "เกณฑ์", value: "criterion", align: "right", width: "80px" },
        { text: "ขาด/เกิน", value: "incomplete", align: "right", width: "90px" }
      ];
    }
  },

  watch: {
    filteredStudentData() {
      this.buildAllCharts();
    },
    filteredManpowerData() {
      this.buildMpDonut();
    },
    subjectMode() {
      this.buildSubjectChart();
    },
    trendMode() {
      this.buildTrendChart();
    },
    searchBranch() {},
    searchCollege() {}
  },

  async mounted() {
    await this.loadStaticData();
    await this.fetchAll();
  },

  methods: {
    formatNum(n) {
      if (n === null || n === undefined) return "-";
      return (+n).toLocaleString("th-TH");
    },
    calcTotal(data) {
      return data.reduce(
        (s, r) =>
          s +
          (+r.rate_work_field_study_lavel_1 || 0) +
          (+r.rate_work_field_study_lavel_2 || 0) +
          (+r.rate_work_field_study_lavel_3 || 0),
        0
      );
    },
    alertColor(t) {
      return {
        success: "#059669",
        warning: "#d97706",
        error: "#dc2626",
        info: "#0891b2"
      }[t];
    },
    alertIcon(t) {
      return {
        success: "mdi-check-circle",
        warning: "mdi-alert",
        error: "mdi-alert-circle",
        info: "mdi-information"
      }[t];
    },
    regionTagColor(name) {
      return REGION_TAGS[name] || "#f3f4f6";
    },

    async loadStaticData() {
      try {
        const [rRes, pRes] = await Promise.all([
          this.$http.post("region.php", { ApiKey: API_KEY }),
          this.$http.post("province.php", { ApiKey: API_KEY })
        ]);
        this.regionOptions = rRes.data;
        this.provinceOptions = pRes.data;
      } catch {
        this.regionOptions = [
          { region_ID: 1, region_name: "ภาคกลาง" },
          { region_ID: 2, region_name: "ภาคเหนือ" },
          { region_ID: 3, region_name: "ภาคตะวันออกเฉียงเหนือ" },
          { region_ID: 4, region_name: "ภาคใต้" },
          { region_ID: 5, region_name: "ภาคตะวันออก" },
          { region_ID: 6, region_name: "ภาคตะวันตก" }
        ];
        this.provinceOptions = [];
      }
    },

    async fetchAll() {
      this.loading = true;
      try {
        const currentYear = +this.filterYear;
        const prevYear = currentYear - 1;
        const prev2Year = currentYear - 2;
        const [d, dp, dp2, mp] = await Promise.all([
          this.fetchStudents(String(currentYear)),
          this.fetchStudents(String(prevYear)),
          this.fetchStudents(String(prev2Year)),
          this.fetchManpower(String(currentYear))
        ]);
        this.studentData = d;
        this.studentDataPrev = dp;
        this.studentDataPrev2 = dp2;
        this.manpowerData = mp;

        const need2566 = String(prev2Year) !== "2566";
        const need2567 = String(prevYear) !== "2567";
        const need2568 = String(currentYear) !== "2568";
        const [fix2566, fix2567, fix2568] = await Promise.all([
          need2566 ? this.fetchStudents("2566") : Promise.resolve(null),
          need2567 ? this.fetchStudents("2567") : Promise.resolve(null),
          need2568 ? this.fetchStudents("2568") : Promise.resolve(null)
        ]);
        this.studentData2566 =
          fix2566 !== null ? fix2566 : String(prev2Year) === "2566" ? dp2 : [];
        this.studentData2567 =
          fix2567 !== null ? fix2567 : String(prevYear) === "2567" ? dp : [];
        this.studentData2568 =
          fix2568 !== null ? fix2568 : String(currentYear) === "2568" ? d : [];
        this.buildAllCharts();
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },

    async fetchStudents(year) {
      try {
        const res = await this.$http.post("rate_work_college_admin.php", {
          ApiKey: API_KEY,
          rate_work_college_year: year,
          action: "list"
        });
        return res.data || [];
      } catch {
        return [];
      }
    },

    async fetchManpower(year) {
      try {
        const res = await this.$http.post("rate_work_g.php", {
          ApiKey: API_KEY,
          rate_work_g_year: year
        });
        return res.data || [];
      } catch {
        return [];
      }
    },

    onFilterChange() {
      // reset filterProvince ถ้าไม่ตรงกับ region ที่เลือก
      if (this.filterRegion && this.filterProvince) {
        const prov = this.provinceOptions.find(
          p => p.province_ID == this.filterProvince
        );
        if (prov && prov.region_ID != this.filterRegion)
          this.filterProvince = null;
      }
      if (!this.filterRegion) this.filterProvince = null;
      // re-fetch เฉพาะถ้าปีเปลี่ยน; ถ้ากรองแค่ region/province/college ใช้ computed แทน
      this.buildAllCharts();
    },
    onRegionChange() {
      this.filterProvince = null;
      this.filterCollege = null;
      this.buildAllCharts();
    },
    onProvinceChange() {
      this.filterCollege = null;
      this.buildAllCharts();
    },
    toggleRegion(row) {
      this.filterRegion =
        this.filterRegion === row.region_ID ? null : row.region_ID;
      if (!this.filterRegion) this.filterProvince = null;
    },
    onProvinceFilter(prov) {
      if (!prov) return;
      if (prov.region_ID) this.filterRegion = prov.region_ID;
      else if (prov.region) {
        const found = this.regionOptions.find(
          r => r.region_name === prov.region
        );
        if (found) this.filterRegion = found.region_ID;
      }
      if (prov.province_ID) this.filterProvince = prov.province_ID;
      else if (prov.province) {
        const found = this.provinceOptions.find(
          p => p.province_name === prov.province
        );
        if (found) this.filterProvince = found.province_ID;
      }
    },

    buildAllCharts() {
      this.buildLevelChart();
      this.buildTrendChart();
      this.buildSubjectChart();
      this.buildMpDonut();
    },

    lightBarOpts(cats, colors, stacked = false, horizontal = false) {
      return {
        chart: {
          background: "transparent",
          foreColor: "#374151",
          toolbar: { show: false },
          stacked
        },
        colors,
        plotOptions: {
          bar: { borderRadius: 4, columnWidth: "60%", horizontal }
        },
        dataLabels: { enabled: false },
        xaxis: {
          categories: cats,
          labels: {
            style: { fontFamily: "Sarabun,sans-serif", fontSize: "11px" },
            rotate: horizontal ? 0 : -30
          }
        },
        yaxis: { labels: { formatter: v => this.formatNum(v) } },
        tooltip: { y: { formatter: v => this.formatNum(v) + " คน" } },
        grid: { borderColor: "#e5e7eb" },
        legend: { fontFamily: "Sarabun,sans-serif" }
      };
    },

    buildLevelChart() {
      const d = this.filteredStudentData,
        groups = {};
      const key = r =>
        this.levelMode === "ภาค"
          ? r.region_name || "ไม่ระบุ"
          : this.levelMode === "ประเภทวิชา"
          ? r.subject_typeName || "ไม่ระบุ"
          : r.province_name || "ไม่ระบุ";
      d.forEach(r => {
        const k = key(r);
        if (!groups[k]) groups[k] = { ch: 0, hs: 0 };
        const v =
          (+r.rate_work_field_study_lavel_1 || 0) +
          (+r.rate_work_field_study_lavel_2 || 0) +
          (+r.rate_work_field_study_lavel_3 || 0);
        if (r.rate_work_college_id_class == 20) groups[k].ch += v;
        else if (r.rate_work_college_id_class == 30) groups[k].hs += v;
      });
      const sorted = Object.entries(groups)
        .sort((a, b) => b[1].ch + b[1].hs - (a[1].ch + a[1].hs))
        .slice(0, 12);
      const cats = sorted.map(([k]) => k);
      this.levelChart = {
        series: [
          { name: "ปวช.", data: sorted.map(([, v]) => v.ch) },
          { name: "ปวส.", data: sorted.map(([, v]) => v.hs) }
        ],
        opts: this.lightBarOpts(cats, ["#2563eb", "#7c3aed"], true)
      };
    },

    buildTrendChart() {
      const years = [
        +this.filterYear - 2,
        +this.filterYear - 1,
        +this.filterYear
      ];
      const all3 = [
        this.studentDataPrev2,
        this.studentDataPrev,
        this.studentData
      ];
      const forecast = linearForecast(all3.map(d => this.calcTotal(d)));
      const base = +this.filterYear;
      const makeSeries = () =>
        this.trendMode === "total"
          ? [
              {
                name: "รวมนักเรียน",
                data: [
                  ...all3.map(d => this.calcTotal(d)),
                  ...Array(3).fill(null)
                ]
              }
            ]
          : [
              {
                name: "ปวช.",
                data: [
                  ...all3.map(d =>
                    this.calcTotal(
                      d.filter(r => r.rate_work_college_id_class == 20)
                    )
                  ),
                  ...Array(3).fill(null)
                ]
              },
              {
                name: "ปวส.",
                data: [
                  ...all3.map(d =>
                    this.calcTotal(
                      d.filter(r => r.rate_work_college_id_class == 30)
                    )
                  ),
                  ...Array(3).fill(null)
                ]
              }
            ];
      const fcSeries = {
        name: "พยากรณ์",
        data: [
          ...Array(2).fill(null),
          this.calcTotal(this.studentData),
          ...forecast
        ]
      };
      this.trendChart = {
        series: [...makeSeries(), fcSeries],
        opts: {
          chart: {
            background: "transparent",
            foreColor: "#374151",
            toolbar: { show: false }
          },
          colors:
            this.trendMode === "total"
              ? ["#2563eb", "#9ca3af"]
              : ["#2563eb", "#7c3aed", "#f59e0b"],
          stroke: { curve: "smooth", width: [3, 3, 2], dashArray: [0, 0, 6] },
          fill: {
            type: "gradient",
            gradient: { opacityFrom: 0.25, opacityTo: 0.03 }
          },
          markers: { size: 5 },
          xaxis: {
            categories: years
              .map(y => `ปี ${y}`)
              .concat([`${base + 1}`, `${base + 2}`, `${base + 3}`]),
            labels: { style: { fontFamily: "Sarabun,sans-serif" } }
          },
          yaxis: { labels: { formatter: v => (v ? this.formatNum(v) : "") } },
          tooltip: {
            y: { formatter: v => (v ? this.formatNum(v) + " คน" : "") }
          },
          grid: { borderColor: "#e5e7eb" },
          legend: { fontFamily: "Sarabun,sans-serif" },
          annotations: {
            xaxis: [
              {
                x: `ปี ${base}`,
                borderColor: "#f59e0b",
                label: {
                  text: "ปัจจุบัน",
                  style: {
                    color: "#fff",
                    background: "#2563eb",
                    fontFamily: "Sarabun,sans-serif"
                  }
                }
              }
            ]
          }
        }
      };
    },

    buildSubjectChart() {
      const d = this.filteredStudentData,
        map = {};
      d.forEach(r => {
        const k = r.subject_typeName || "ไม่ระบุ";
        if (!map[k]) map[k] = { ch: 0, hs: 0 };
        const v =
          (+r.rate_work_field_study_lavel_1 || 0) +
          (+r.rate_work_field_study_lavel_2 || 0) +
          (+r.rate_work_field_study_lavel_3 || 0);
        if (r.rate_work_college_id_class == 20) map[k].ch += v;
        else if (r.rate_work_college_id_class == 30) map[k].hs += v;
      });
      const mode = this.subjectMode;
      const sorted = Object.entries(map)
        .map(([k, v]) => ({
          k,
          val: mode === "ปวช." ? v.ch : mode === "ปวส." ? v.hs : v.ch + v.hs
        }))
        .sort((a, b) => b.val - a.val)
        .slice(0, 10);
      const cats = sorted.map(r => r.k);
      const color =
        mode === "ปวช." ? "#2563eb" : mode === "ปวส." ? "#7c3aed" : "#0891b2";
      this.subjectChart = {
        series: [{ name: mode, data: sorted.map(r => r.val) }],
        opts: {
          ...this.lightBarOpts(cats, [color], false, true),
          plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
          yaxis: {
            labels: {
              style: { fontFamily: "Sarabun,sans-serif", fontSize: "11px" },
              maxWidth: 160
            }
          },
          xaxis: {
            categories: cats,
            labels: { formatter: v => this.formatNum(v) }
          }
        }
      };
    },

    buildMpDonut() {
      const confirmed = this.filteredManpowerData.filter(
        r => r.rate_work_g_status === "Confirm"
      ).length;
      const inprogress = this.filteredManpowerData.filter(
        r => r.rate_work_g_status === "Save"
      ).length;
      const total = this.filteredManpowerData.length || 1;
      const pct = Math.round((confirmed / total) * 100);
      this.mpDonut = {
        series: [
          pct,
          Math.round((inprogress / total) * 100),
          Math.round(((total - confirmed - inprogress) / total) * 100)
        ],
        opts: {
          chart: {
            background: "transparent",
            foreColor: "#374151",
            toolbar: { show: false }
          },
          colors: ["#059669", "#d97706", "#dc2626"],
          labels: ["เรียบร้อย", "กำลังดำเนิน", "ยังไม่ดำเนิน"],
          plotOptions: {
            radialBar: {
              hollow: { size: "38%" },
              dataLabels: {
                total: {
                  show: true,
                  label: "รายงาน",
                  formatter: () => `${pct}%`,
                  style: { color: "#374151", fontFamily: "Sarabun,sans-serif" }
                }
              }
            }
          },
          legend: { show: false }
        }
      };
    },

    // ── Export xlsx (ครบทุก sheet รวม Branch Insight) ──
    exportXlsx() {
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(
          this.filteredStudentData.map(r => ({
            ปีการศึกษา: r.rate_work_college_year,     
            ชื่อวิทยาลัย: r.college_name,
            จังหวัด: r.province_name,
            ภาค: r.region_name,
            ประเภทวิชา: r.subject_typeName,
            ระดับ:
              r.rate_work_college_id_class == 20
                ? "ปวช."
                : r.rate_work_college_id_class == 30
                ? "ปวส."
                : "อื่นๆ",
            สาขาวิชา: r.name_type_course,
            "ชั้นปีที่ 1": r.rate_work_field_study_lavel_1,
            "ชั้นปีที่ 2": r.rate_work_field_study_lavel_2,
            "ชั้นปีที่ 3": r.rate_work_field_study_lavel_3,
            รวม:
              (+r.rate_work_field_study_lavel_1 || 0) +
              (+r.rate_work_field_study_lavel_2 || 0) +
              (+r.rate_work_field_study_lavel_3 || 0)
          }))
        ),
        "ข้อมูลนักเรียน"
      );

      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(
          this.regionSummary.map(r => ({
            ภาค: r.region_name,
            นักเรียนรวม: r.students,
            "ปวช.": r.ch,
            "ปวส.": r.hs,
            วิทยาลัย: r.colleges,
            "%": r.pct
          }))
        ),
        "สรุปรายภาค"
      );

      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(
          this.filteredBranches.map(b => ({
            สาขาวิชา: b.name,
            ประเภทวิชา: b.subjectType,
            "ปวช.": b.ch,
            "ปวส.": b.hs,
            รวม: b.total,
            วิทยาลัย: b.colleges,
            "สัดส่วน(%)": b.pctOfTotal
          }))
        ),
        "สาขาวิชา"
      );

      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(
          this.topBranches.map((b, i) => ({
            อันดับ: i + 1,
            สาขาวิชา: b.name,
            ประเภทวิชา: b.subjectType,
            "ปวช.": b.ch,
            "ปวส.": b.hs,
            รวม: b.total
          }))
        ),
        "Top10 สาขาวิชา"
      );

      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(
          this.branchTeacherRatio.map((b, i) => ({
            อันดับ: i + 1,
            สาขาวิชา: b.name,
            ประเภทวิชา: b.subjectType,
            อัตราส่วนครู_นร: `1:${b.ratio}`,
            นักเรียน: b.students,
            ครู: b.teachers
          }))
        ),
        "อัตราครู-นร รายสาขา"
      );

      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(
          this.filteredCollegeRows.map(c => ({
            รหัส: c.code,
            ชื่อ: c.name,
            จังหวัด: c.province,
            ภาค: c.region,
            "ปวช.": c.ch,
            "ปวส.": c.hs,
            รวม: c.total,
            สาขา: c.branches,
            อัตรากำลัง: c.manpower,
            เกณฑ์: c.criterion,
            "ขาด/เกิน": c.incomplete
          }))
        ),
        "สถานศึกษา"
      );

      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(
          this.filteredManpowerData.map(r => ({
            รหัส: r.rate_work_g_college_code,
            สถานศึกษา: r.college_name,
            จังหวัด: r.province_name,
            ปี: r.rate_work_g_year,
            สถานะ: r.rate_work_g_status,
            ข้าราชการครู: r.rate_teach_official_all,
            พนักงานราชการ: r.rate_teach_civil_all,
            อัตรากำลังรวม: r.rate_man_power_all,
            เกณฑ์รวม: r.rate_criterion_all,
            "ขาด/เกิน": r.rate_criterion_incomplete_all
          }))
        ),
        "อัตรากำลัง"
      );

      XLSX.writeFile(
        wb,
        `ข้อมูลอาชีวศึกษา_${this.filterYear}_ภาค${this.filterSemester}.xlsx`
      );
    },

    exportBranchXlsx() {
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(
          this.filteredBranches.map(b => ({
            สาขาวิชา: b.name,
            ประเภทวิชา: b.subjectType,
            "ปวช.": b.ch,
            "ปวส.": b.hs,
            รวม: b.total,
            วิทยาลัย: b.colleges,
            "สัดส่วน(%)": b.pctOfTotal
          }))
        ),
        "สาขาวิชา"
      );
      XLSX.writeFile(wb, `สาขาวิชา_${this.filterYear}.xlsx`);
    },

    exportCollegeXlsx() {
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(
          this.filteredCollegeRows.map(c => ({
            รหัส: c.code,
            ชื่อ: c.name,
            จังหวัด: c.province,
            ภาค: c.region,
            "ปวช.": c.ch,
            "ปวส.": c.hs,
            รวม: c.total,
            สาขา: c.branches,
            อัตรากำลัง: c.manpower,
            เกณฑ์: c.criterion,
            "ขาด/เกิน": c.incomplete
          }))
        ),
        "สถานศึกษา"
      );
      XLSX.writeFile(wb, `สถานศึกษา_${this.filterYear}.xlsx`);
    }
  }
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Sarabun:wght@300;400;500;600;700;800&family=IBM+Plex+Mono:wght@400;600&display=swap");

* {
  box-sizing: border-box;
}
.exec-dash {
  font-family: "Sarabun", sans-serif;
  background: #f0f4fa;
  color: #1e293b;
  min-height: 100vh;
}

/* ══ HEADER ══ */
.hdr-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(135deg, #1e40af 0%, #3730a3 60%, #4f46e5 100%);
  padding: 12px 20px;
  position: sticky;
  top: 0;
  z-index: 100;
}
.hdr-left {
  display: flex;
  align-items: center;
  gap: 12px;
}
.hdr-icon-wrap {
  width: 42px;
  height: 42px;
  background: rgba(255, 255, 255, 0.18);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.hdr-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #fff;
}
.hdr-sub {
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.75);
  margin-top: 2px;
}
.hdr-right {
  display: flex;
  align-items: center;
  gap: 10px;
}
.hdr-pill {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 5px 12px;
  border-radius: 999px;
  font-size: 0.75rem;
}
.hdr-pill--year {
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
}
.hdr-pill--count {
  background: rgba(255, 255, 255, 0.22);
  color: #fff;
}

/* ══ FILTER BAR ══ */
.filter-bar {
  background: #fff;
  border-bottom: 1px solid #e2e8f0;
  padding: 10px 20px;
}
.filter-inner {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}
.filter-group {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.filter-label {
  font-size: 0.65rem;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.f-sel {
  width: 110px !important;
}
.f-sel--md {
  width: 140px !important;
}
.f-sel--lg {
  width: 180px !important;
}
.filter-actions {
  display: flex;
  gap: 8px;
  margin-left: auto;
}
.btn-refresh {
  background: #f0f9ff !important;
  color: #0369a1 !important;
}
.btn-export {
  background: #f0fdf4 !important;
  color: #15803d !important;
}

/* ══ LOADING ══ */
.loading-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}
.loading-txt {
  color: #fff;
  font-size: 0.85rem;
}

/* ══ BODY ══ */
.dash-body {
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

/* ══ KPI ROW ══ */
.kpi-row {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}
.kpi-card {
  flex: 1;
  min-width: 160px;
  border-radius: 12px;
  padding: 14px 16px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  background: #fff;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.07);
}
.kpi-card--blue {
  border-top: 3px solid #2563eb;
}
.kpi-card--purple {
  border-top: 3px solid #7c3aed;
}
.kpi-card--teal {
  border-top: 3px solid #0891b2;
}
.kpi-card--orange {
  border-top: 3px solid #ea580c;
}
.kpi-card--indigo {
  border-top: 3px solid #4338ca;
}
.kpi-card--rose {
  border-top: 3px solid #e11d48;
}
.kpi-card-body {
  flex: 1;
}
.kpi-lbl {
  font-size: 0.72rem;
  color: #6b7280;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.kpi-val {
  font-size: 1.6rem;
  font-weight: 800;
  color: #1e293b;
  line-height: 1.2;
  margin: 4px 0;
}
.kpi-sub {
  font-size: 0.73rem;
  color: #6b7280;
}
.kpi-sub-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 2px;
}
.kpi-trend {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  font-size: 0.72rem;
}
.trend-pos {
  color: #059669;
  font-weight: 700;
}
.trend-neg {
  color: #dc2626;
  font-weight: 700;
}
.trend-ref {
  color: #9ca3af;
}
.kpi-icon-wrap {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.kpi-icon-wrap--blue {
  background: #2563eb;
}
.kpi-icon-wrap--purple {
  background: #7c3aed;
}
.kpi-icon-wrap--teal {
  background: #0891b2;
}
.kpi-icon-wrap--orange {
  background: #ea580c;
}
.kpi-icon-wrap--indigo {
  background: #4338ca;
}
.kpi-icon-wrap--rose {
  background: #e11d48;
}

/* ══ DASH ROW ══ */
.dash-row {
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
}

/* ══ PANEL ══ */
.panel {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.07);
  overflow: hidden;
  min-width: 0;
}
.panel-hd {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  border-bottom: 1px solid #f1f5f9;
  font-size: 0.8rem;
  font-weight: 700;
  color: #1e293b;
}
.tab-grp {
  display: flex;
  background: #f1f5f9;
  border-radius: 6px;
  padding: 2px;
  gap: 2px;
}
.tab-btn {
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 0.7rem;
  cursor: pointer;
  color: #6b7280;
  font-weight: 600;
  transition: all 0.15s;
}
.tab-btn.active {
  background: #fff;
  color: #2563eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
}
.no-data {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100px;
  color: #9ca3af;
  font-size: 0.8rem;
}

/* ══ REGION TABLE ══ */
.tbl-wrap {
  overflow-x: auto;
}
.reg-tbl {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.78rem;
}
.reg-tbl th {
  padding: 8px 10px;
  font-size: 0.63rem;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  white-space: nowrap;
  background: #f8fafc;
  border-bottom: 2px solid #e2e8f0;
}
.reg-tbl td {
  padding: 8px 10px;
  border-bottom: 1px solid #f1f5f9;
}
.reg-tr {
  cursor: pointer;
}
.reg-tr:hover {
  background: #f0f9ff;
}
.reg-tr--active {
  background: #eff6ff !important;
}
.reg-total {
  background: #f8fafc;
  font-weight: 700;
  border-top: 2px solid #e2e8f0;
}
.rdot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
}
.rbar-bg {
  height: 5px;
  background: #e5e7eb;
  border-radius: 3px;
  overflow: hidden;
}
.rbar-fill {
  height: 5px;
  border-radius: 3px;
  transition: width 0.4s;
}
.rpct {
  font-size: 0.68rem;
  color: #6b7280;
}

/* ══ MP BREAKDOWN ══ */
.mp-breakdown {
  padding: 10px 14px 14px;
}
.mp-bk-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
}
.mp-bk-left {
  display: flex;
  align-items: center;
  flex: 1;
}
.mp-bk-lbl {
  font-size: 0.76rem;
  color: #374151;
}
.mp-bk-right {
  display: flex;
  align-items: center;
  gap: 8px;
}
.mp-bk-val {
  font-size: 0.78rem;
  font-weight: 700;
  color: #1e293b;
  min-width: 55px;
  text-align: right;
}
.mp-bk-bar-bg {
  width: 80px;
  height: 6px;
  background: #e5e7eb;
  border-radius: 3px;
  overflow: hidden;
}
.mp-bk-bar-fill {
  height: 6px;
  border-radius: 3px;
  transition: width 0.4s;
}
.criterion-box {
  background: #f8fafc;
  border-radius: 8px;
  padding: 8px 12px;
  margin-top: 8px;
}
.criterion-row {
  display: flex;
  justify-content: space-between;
  font-size: 0.75rem;
  color: #374151;
  padding: 3px 0;
  border-bottom: 1px solid #e2e8f0;
}
.criterion-row:last-child {
  border-bottom: none;
}

/* ══ MP LEGEND ══ */
.mp-stat-leg {
  padding: 8px 14px 12px;
}
.mp-leg-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 3px 0;
  font-size: 0.76rem;
}
.mp-leg-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
.mp-leg-lbl {
  flex: 1;
  color: #374151;
}
.mp-leg-val {
  font-weight: 700;
  color: #1e293b;
}

/* ══ DONUT LEG ══ */
.donut-leg {
  display: flex;
  justify-content: center;
  gap: 20px;
  padding: 6px 14px 12px;
}
.dl-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.76rem;
  color: #374151;
}
.dl-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

/* ══ INSIGHT BAR ══ */
.insight-bar {
  display: flex;
  align-items: center;
  padding: 6px 14px 10px;
  font-size: 0.74rem;
  color: #374151;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
}

/* ══ MODE TOGGLE ══ */
.mode-toggle .v-btn {
  min-width: 52px !important;
}
.mode-toggle .v-btn--active {
  background: #2563eb !important;
  color: #fff !important;
}

/* ══ BRANCH INSIGHT PANEL ══ */
.insight-list {
  padding: 8px 14px 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 320px;
  overflow-y: auto;
}
.insight-caption {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.68rem;
  color: #9ca3af;
  margin-bottom: 4px;
}
.insight-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 8px;
  border-radius: 8px;
  background: #f8fafc;
}
.insight-row--dim {
  opacity: 0.85;
}
.insight-row:hover {
  background: #eff6ff;
}
.insight-rank {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.68rem;
  font-weight: 700;
  flex-shrink: 0;
}
.rank-gold {
  background: #fef3c7;
  color: #b45309;
}
.rank-normal {
  background: #e0e7ff;
  color: #4338ca;
}
.rank-red {
  background: #fee2e2;
  color: #dc2626;
}
.insight-info {
  flex: 1;
  min-width: 0;
}
.insight-name {
  font-size: 0.76rem;
  font-weight: 600;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.insight-sub {
  font-size: 0.65rem;
  color: #6b7280;
}
.insight-total {
  font-weight: 700;
  font-size: 0.78rem;
  color: #1e293b;
  min-width: 55px;
  text-align: right;
}
.insight-bars {
  flex: 1.5;
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.ibar-row {
  display: flex;
  align-items: center;
  gap: 4px;
}
.ibar-lbl {
  font-size: 0.62rem;
  font-weight: 700;
  width: 24px;
  flex-shrink: 0;
}
.ibar-bg {
  flex: 1;
  height: 5px;
  background: #e5e7eb;
  border-radius: 3px;
  overflow: hidden;
}
.ibar-fill {
  height: 5px;
  border-radius: 3px;
}
.ibar-fill--blue {
  background: #2563eb;
}
.ibar-fill--purple {
  background: #7c3aed;
}
.ibar-val {
  font-size: 0.64rem;
  color: #374151;
  min-width: 38px;
  text-align: right;
}

/* อัตราส่วนครู badge */
.ratio-badge {
  font-size: 0.72rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 999px;
  flex-shrink: 0;
}
.ratio-green {
  background: #d1fae5;
  color: #065f46;
}
.ratio-amber {
  background: #fef3c7;
  color: #92400e;
}
.ratio-red {
  background: #fee2e2;
  color: #991b1b;
}

/* ══ V-DATA-TABLE overrides ══ */
.branch-dt >>> .v-data-table__wrapper,
.college-dt >>> .v-data-table__wrapper {
  font-family: "Sarabun", sans-serif !important;
  font-size: 0.79rem !important;
}
.branch-dt >>> th,
.college-dt >>> th {
  font-size: 0.65rem !important;
  font-weight: 700 !important;
  color: #6b7280 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.3px !important;
  background: #f8fafc !important;
  white-space: nowrap;
}
.branch-dt >>> td,
.college-dt >>> td {
  font-size: 0.79rem !important;
  color: #374151 !important;
  border-bottom: 1px solid #f1f5f9 !important;
}
.branch-dt >>> tr:hover td,
.college-dt >>> tr:hover td {
  background: #f5f3ff !important;
}
.branch-dt >>> .v-data-footer,
.college-dt >>> .v-data-footer {
  font-family: "Sarabun", sans-serif !important;
  font-size: 0.73rem !important;
  border-top: 1px solid #e2e8f0 !important;
}
.branch-name-cell {
  max-width: 200px;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
  color: #1e293b;
}
.branch-search {
  max-width: 200px !important;
}
.branch-search >>> .v-input__slot {
  min-height: 30px !important;
  background: #f8fafc !important;
}
.pct-bar-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
}
.pct-bar-fill {
  height: 6px;
  background: #818cf8;
  border-radius: 3px;
  min-width: 2px;
}
.pct-val {
  font-size: 0.7rem;
  color: #6b7280;
}

/* ══ BADGES ══ */
.badge-red {
  background: #fee2e2;
  color: #dc2626;
  border-radius: 999px;
  padding: 1px 7px;
  font-size: 0.72rem;
  font-weight: 700;
}
.badge-green {
  background: #d1fae5;
  color: #059669;
  border-radius: 999px;
  padding: 1px 7px;
  font-size: 0.72rem;
  font-weight: 700;
}
.badge-gray {
  background: #e5e7eb;
  color: #6b7280;
  border-radius: 999px;
  padding: 1px 7px;
  font-size: 0.72rem;
  font-weight: 700;
}
.college-badge {
  background: #e0e7ff;
  color: #4338ca;
  border-radius: 999px;
  padding: 1px 7px;
  font-size: 0.68rem;
  font-weight: 700;
}
.college-nm {
  font-weight: 600;
  color: #1e293b;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
}
.region-tag {
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.65rem;
  font-weight: 700;
  color: #374151;
}
.mono-sm {
  font-family: "IBM Plex Mono", monospace;
  font-size: 0.73rem;
  color: #6b7280;
}

/* ══ CHALLENGES & TRANSFORMATIONS ══ */
.list-body {
  padding: 10px 14px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.list-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 0.78rem;
  color: #374151;
  line-height: 1.5;
}
.list-num {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 700;
  flex-shrink: 0;
}
.list-num--red {
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
}
.list-title {
  font-weight: 700;
  color: #1e293b;
  font-size: 0.78rem;
}
.list-desc {
  font-size: 0.68rem;
  color: #6b7280;
}

/* ══ ALERTS ══ */
.alert-body {
  padding: 10px 14px 14px;
  display: flex;
  flex-direction: column;
  gap: 7px;
}
.alert-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 9px 12px;
  border-radius: 8px;
}
.alert-item--success {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
}
.alert-item--warning {
  background: #fffbeb;
  border: 1px solid #fde68a;
}
.alert-item--error {
  background: #fef2f2;
  border: 1px solid #fecaca;
}
.alert-item--info {
  background: #f0f9ff;
  border: 1px solid #bae6fd;
}
.alert-title {
  font-size: 0.78rem;
  font-weight: 700;
  color: #1e293b;
}
.alert-desc {
  font-size: 0.68rem;
  color: #6b7280;
  margin-top: 1px;
}

/* ══ UTILITY ══ */
.ta-r {
  text-align: right !important;
}
.fw5 {
  font-weight: 500;
}
.fw6 {
  font-weight: 700;
}
.c-blue {
  color: #2563eb !important;
}
.c-purple {
  color: #7c3aed !important;
}
.c-green {
  color: #059669 !important;
}
.c-red {
  color: #dc2626 !important;
}

/* ══ FOOTER ══ */
.dash-footer {
  display: flex;
  justify-content: space-between;
  padding: 8px 2px;
  font-size: 0.67rem;
  color: #9ca3af;
  border-top: 1px solid #e2e8f0;
  margin-top: 4px;
}

/* ══ RESPONSIVE ══ */
@media (max-width: 960px) {
  .dash-row {
    flex-direction: column;
  }
  .kpi-row {
    gap: 8px;
  }
  .kpi-card {
    min-width: 150px;
  }
  .filter-bar {
    padding: 8px 12px;
  }
  .dash-body {
    padding: 12px;
  }
  .kpi-val {
    font-size: 1.35rem;
  }
}
@media (max-width: 600px) {
  .hdr-bar {
    padding: 10px 12px;
  }
  .hdr-title {
    font-size: 0.9rem;
  }
  .f-sel,
  .f-sel--md {
    width: 90px !important;
  }
  .kpi-card {
    min-width: 140px;
  }
}
</style>
